import { c as _c } from "react/compiler-runtime";
import type { HofDashboardData } from '@/features/hof-dashboard/api/hof-dashboard-data';
import { AREA_DOT_CLASS, AREA_TEXT_CLASS, Panel, ProgressBar, ProgressLine, ResponsibleList, SectionHeading } from '@/features/hof-dashboard/components/dashboard-ui';
import { DeadlineList } from '@/features/hof-dashboard/components/deadline-list';
import { HOF_DASHBOARD_AREA_LABELS, HOF_DASHBOARD_AREAS, type HofDashboardArea } from '@/features/hof-dashboard/constants';
import { translate } from '@/features/hof-dashboard/texts';
import { isOpen, percentDone, type SubmissionProgress } from '@/features/hof-dashboard/utils/submission-progress';
import type { Locale } from '@/types/types';
import { cn } from '@/utils/tailwindcss-override';
import { CheckCircle2, ChevronRight } from 'lucide-react';
import type React from 'react';

/** Which area a submission belongs to, marked by the area's colour but read as plain text. */
const AreaTag: React.FC<{
  area: HofDashboardArea;
  locale: Locale;
}> = t0 => {
  const $ = _c(7);
  const {
    area,
    locale
  } = t0;
  const t1 = AREA_DOT_CLASS[area];
  let t2;
  if ($[0] !== t1) {
    t2 = cn("h-2 w-2 rounded-full", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t2) {
    t3 = <span className={t2} aria-hidden={true} />;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = HOF_DASHBOARD_AREA_LABELS[area][locale];
  let t5;
  if ($[4] !== t3 || $[5] !== t4) {
    t5 = <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-600">{t3}{t4}</span>;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  return t5;
};

/** Soonest first; a form without a deadline goes last. */
const byDeadline = (a: SubmissionProgress, b: SubmissionProgress): number => (a.deadline ?? '9999').localeCompare(b.deadline ?? '9999');

/**
 * What the Hof sees first: what is due next, how far it is, when the deadlines are and whom
 * to ask, in that order, so the first screen of a phone answers "what is missing?".
 */
export const OverviewView: React.FC<{
  data: HofDashboardData;
  progress: Record<string, SubmissionProgress>;
  locale: Locale;
  onOpen: (area: HofDashboardArea, formId: string) => void;
}> = t0 => {
  const $ = _c(60);
  const {
    data,
    progress,
    locale,
    onOpen
  } = t0;
  let T0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== data.forms || $[1] !== locale || $[2] !== onOpen || $[3] !== progress) {
    let t6;
    if ($[10] !== progress) {
      t6 = form => {
        const formProgress = progress[form.id];
        return formProgress === undefined || !isOpen(formProgress) ? [] : [{
          form,
          progress: formProgress
        }];
      };
      $[10] = progress;
      $[11] = t6;
    } else {
      t6 = $[11];
    }
    const open = data.forms.flatMap(t6).toSorted(_temp);
    let t7;
    if ($[12] !== data.forms) {
      t7 = area => data.forms.some(form_0 => form_0.area === area);
      $[12] = data.forms;
      $[13] = t7;
    } else {
      t7 = $[13];
    }
    const areas = HOF_DASHBOARD_AREAS.filter(t7);
    t4 = "grid items-start gap-6 @3xl:grid-cols-2 @6xl:grid-cols-3";
    let t8;
    if ($[14] !== locale) {
      t8 = translate("nextUp", locale);
      $[14] = locale;
      $[15] = t8;
    } else {
      t8 = $[15];
    }
    let t9;
    if ($[16] !== t8) {
      t9 = <SectionHeading className="mb-2">{t8}</SectionHeading>;
      $[16] = t8;
      $[17] = t9;
    } else {
      t9 = $[17];
    }
    const t10 = open.length === 0 ? <p className="flex items-center gap-2 text-sm text-green-600"><CheckCircle2 className="h-4 w-4" aria-hidden={true} />{translate("allDone", locale)}</p> : <ul className="-mx-5 divide-y divide-gray-100 border-y border-gray-100 @xl:-mx-6">{open.map(t11 => {
        const {
          form: form_1,
          progress: formProgress_0
        } = t11;
        return <li key={form_1.id}><button type="button" className="flex min-h-11 w-full cursor-pointer items-center justify-between gap-3 px-5 py-3 text-left transition hover:bg-gray-50 focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-green-600 @xl:px-6" onClick={() => onOpen(form_1.area, form_1.id)}><span className="min-w-0 space-y-1"><span className="flex flex-wrap items-baseline gap-x-2"><span className="text-sm font-semibold text-gray-900">{form_1.title}</span><AreaTag area={form_1.area} locale={locale} /></span><ProgressLine progress={formProgress_0} locale={locale} /></span><ChevronRight className="h-5 w-5 shrink-0 text-gray-500" aria-hidden={true} /></button></li>;
      })}</ul>;
    if ($[18] !== t10 || $[19] !== t9) {
      t5 = <Panel className="@3xl:col-span-2">{t9}{t10}</Panel>;
      $[18] = t10;
      $[19] = t9;
      $[20] = t5;
    } else {
      t5 = $[20];
    }
    T0 = Panel;
    t1 = "space-y-5";
    let t12;
    if ($[21] !== locale) {
      t12 = translate("progress", locale);
      $[21] = locale;
      $[22] = t12;
    } else {
      t12 = $[22];
    }
    if ($[23] !== t12) {
      t2 = <SectionHeading>{t12}</SectionHeading>;
      $[23] = t12;
      $[24] = t2;
    } else {
      t2 = $[24];
    }
    let t13;
    if ($[25] !== data.forms || $[26] !== locale || $[27] !== progress) {
      t13 = area_0 => {
        const states = data.forms.filter(form_2 => form_2.area === area_0).flatMap(form_3 => progress[form_3.id]?.state ?? []);
        const count = translate("progressCount", locale, {
          done: states.filter(_temp2).length,
          total: states.length
        });
        return <div key={area_0} className="space-y-1.5"><div className="flex items-baseline justify-between gap-3 text-sm"><span className={cn("font-semibold", AREA_TEXT_CLASS[area_0])}>{HOF_DASHBOARD_AREA_LABELS[area_0][locale]}</span><span className="text-gray-600 tabular-nums">{count}</span></div><ProgressBar percent={percentDone(states)} area={area_0} label={`${HOF_DASHBOARD_AREA_LABELS[area_0][locale]}: ${count}`} /></div>;
      };
      $[25] = data.forms;
      $[26] = locale;
      $[27] = progress;
      $[28] = t13;
    } else {
      t13 = $[28];
    }
    t3 = areas.map(t13);
    $[0] = data.forms;
    $[1] = locale;
    $[2] = onOpen;
    $[3] = progress;
    $[4] = T0;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    T0 = $[4];
    t1 = $[5];
    t2 = $[6];
    t3 = $[7];
    t4 = $[8];
    t5 = $[9];
  }
  let t6;
  if ($[29] !== T0 || $[30] !== t1 || $[31] !== t2 || $[32] !== t3) {
    t6 = <T0 className={t1}>{t2}{t3}</T0>;
    $[29] = T0;
    $[30] = t1;
    $[31] = t2;
    $[32] = t3;
    $[33] = t6;
  } else {
    t6 = $[33];
  }
  let t7;
  if ($[34] !== locale) {
    t7 = translate("deadlines", locale);
    $[34] = locale;
    $[35] = t7;
  } else {
    t7 = $[35];
  }
  let t8;
  if ($[36] !== t7) {
    t8 = <SectionHeading>{t7}</SectionHeading>;
    $[36] = t7;
    $[37] = t8;
  } else {
    t8 = $[37];
  }
  let t9;
  if ($[38] !== data.deadlines || $[39] !== locale) {
    t9 = <DeadlineList deadlines={data.deadlines} locale={locale} />;
    $[38] = data.deadlines;
    $[39] = locale;
    $[40] = t9;
  } else {
    t9 = $[40];
  }
  let t10;
  if ($[41] !== t8 || $[42] !== t9) {
    t10 = <Panel className="space-y-4">{t8}{t9}</Panel>;
    $[41] = t8;
    $[42] = t9;
    $[43] = t10;
  } else {
    t10 = $[43];
  }
  let t11;
  if ($[44] !== locale) {
    t11 = translate("responsible", locale);
    $[44] = locale;
    $[45] = t11;
  } else {
    t11 = $[45];
  }
  let t12;
  if ($[46] !== t11) {
    t12 = <SectionHeading>{t11}</SectionHeading>;
    $[46] = t11;
    $[47] = t12;
  } else {
    t12 = $[47];
  }
  let t13;
  if ($[48] !== data.responsible || $[49] !== locale) {
    t13 = <ResponsibleList people={data.responsible} locale={locale} />;
    $[48] = data.responsible;
    $[49] = locale;
    $[50] = t13;
  } else {
    t13 = $[50];
  }
  let t14;
  if ($[51] !== t12 || $[52] !== t13) {
    t14 = <Panel className="space-y-4 @3xl:col-span-2">{t12}{t13}</Panel>;
    $[51] = t12;
    $[52] = t13;
    $[53] = t14;
  } else {
    t14 = $[53];
  }
  let t15;
  if ($[54] !== t10 || $[55] !== t14 || $[56] !== t4 || $[57] !== t5 || $[58] !== t6) {
    t15 = <div className={t4}>{t5}{t6}{t10}{t14}</div>;
    $[54] = t10;
    $[55] = t14;
    $[56] = t4;
    $[57] = t5;
    $[58] = t6;
    $[59] = t15;
  } else {
    t15 = $[59];
  }
  return t15;
};
function _temp(a, b) {
  return byDeadline(a.progress, b.progress);
}
function _temp2(state) {
  return state === "done";
}